import React, { useEffect } from 'react';
import { Modal, Form, InputNumber, DatePicker, Input, Alert, Space, Button } from 'antd';
import { DollarOutlined, CloseOutlined } from '@ant-design/icons';
import { formatCurrency } from '../../../../shared/utils/formatters';
import dayjs from 'dayjs';
import styles from './PaymentModal.module.css';

const { TextArea } = Input;

export default function PaymentModal({
    visible,
    debt,
    onSubmit,
    onCancel,
    loading = false
}) {
    const [form] = Form.useForm();

    useEffect(() => {
        if (visible && debt) {
            const remainingAmount = debt.soTien - (debt.soTienDaTra || 0);
            form.setFieldsValue({
                ngayTra: dayjs(),
                soTienTra: remainingAmount,
                ghiChu: ''
            });
        }
    }, [visible, debt, form]);

    const handleSubmit = async (values) => {
        try {
            const paymentData = {
                debtId: debt.id,
                nguoiVay: debt.nguoiVay,
                soTienTra: values.soTienTra,
                ngayTra: values.ngayTra.toDate(),
                ghiChu: values.ghiChu || ''
            };

            await onSubmit(paymentData);
            form.resetFields();
        } catch (error) {
            console.error('Error submitting payment:', error);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    if (!debt) return null;

    const remainingAmount = debt.soTien - (debt.soTienDaTra || 0);
    const paidAmount = debt.soTienDaTra || 0;

    return (
        <Modal
            title={
                <div className={styles.modalTitle}>
                    <DollarOutlined />
                    <span>Thanh toán cho {debt.nguoiVay}</span>
                </div>
            }
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={500}
            className={styles.paymentModal}
        >
            <div className={styles.debtInfo}>
                <Alert
                    message={
                        <div className={styles.debtSummary}>
                            <div className={styles.summaryRow}>
                                <span>Tổng nợ:</span>
                                <strong>{formatCurrency(debt.soTien)}</strong>
                            </div>
                            <div className={styles.summaryRow}>
                                <span>Đã trả:</span>
                                <strong className={styles.paidAmount}>
                                    {formatCurrency(paidAmount)}
                                </strong>
                            </div>
                            <div className={styles.summaryRow}>
                                <span>Còn lại:</span>
                                <strong className={styles.remainingAmount}>
                                    {formatCurrency(remainingAmount)}
                                </strong>
                            </div>
                        </div>
                    }
                    type="info"
                    className={styles.debtAlert}
                />
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                className={styles.paymentForm}
            >
                <Form.Item
                    name="soTienTra"
                    label="Số tiền trả"
                    rules={[
                        { required: true, message: 'Vui lòng nhập số tiền!' },
                        {
                            validator: (_, value) => {
                                if (value && value > remainingAmount) {
                                    return Promise.reject('Số tiền trả không được vượt quá số tiền còn lại!');
                                }
                                if (value && value <= 0) {
                                    return Promise.reject('Số tiền trả phải lớn hơn 0!');
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                >
                    <InputNumber
                        style={{ width: '100%' }}
                        placeholder="Nhập số tiền trả"
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        min={0}
                        max={remainingAmount}
                        addonAfter="VNĐ"
                    />
                </Form.Item>

                <Form.Item
                    name="ngayTra"
                    label="Ngày trả"
                    rules={[{ required: true, message: 'Vui lòng chọn ngày trả!' }]}
                >
                    <DatePicker
                        style={{ width: '100%' }}
                        format="DD/MM/YYYY HH:mm"
                        showTime={{
                            format: 'HH:mm',
                            defaultValue: dayjs().hour(12).minute(0)
                        }}
                        placeholder="Chọn ngày và giờ trả"
                    />
                </Form.Item>

                <Form.Item
                    name="ghiChu"
                    label="Ghi chú"
                >
                    <TextArea
                        rows={3}
                        placeholder="Ghi chú về lần thanh toán này (tùy chọn)..."
                        maxLength={200}
                        showCount
                    />
                </Form.Item>

                <Form.Item className={styles.formActions}>
                    <Space>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            icon={<DollarOutlined />}
                            size="large"
                        >
                            Thanh toán
                        </Button>
                        <Button
                            onClick={handleCancel}
                            icon={<CloseOutlined />}
                            size="large"
                        >
                            Hủy
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
}
